import { useEffect, useState } from "react";
import { api, apiErr } from "@/admin/api";
import { PageTitle } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, GripVertical, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Booking", "Deposits", "Cancellations", "Group Travel", "Cruises", "Resort Stays", "Travel List", "General KIS Living"];
const EMPTY = { question: "", answer: "", category: "General KIS Living", visible: true };

export default function FaqManager() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/faqs"); setItems(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (f) => { setForm({ ...EMPTY, ...f }); setEditingId(f.id); setOpen(true); };

  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) { toast.error("Question and answer are required."); return; }
    setSaving(true);
    try {
      if (editingId) await api.patch(`/admin/faqs/${editingId}`, form);
      else await api.post("/admin/faqs", form);
      toast.success(editingId ? "FAQ updated" : "FAQ added");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const toggleVisible = async (f) => {
    try { await api.patch(`/admin/faqs/${f.id}`, { ...f, visible: !f.visible }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (f) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try { await api.delete(`/admin/faqs/${f.id}`); toast.success("FAQ deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ids = items.map((i) => i.id);
    const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setDragId(null);
    // optimistic reorder
    setItems(ids.map((id) => items.find((i) => i.id === id)));
    try { await api.post("/admin/faqs/reorder", { ids }); }
    catch (e) { toast.error(apiErr(e)); load(); }
  };

  return (
    <div data-testid="admin-faqs">
      <PageTitle title="FAQ Manager" subtitle="Add, edit, reorder and categorize your homepage FAQs">
        <Button data-testid="new-faq" onClick={openNew} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">
          <Plus size={16} /> New FAQ
        </Button>
      </PageTitle>

      {!items ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-terracotta" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-bone p-16 text-center"><p className="text-ink-soft">No FAQs yet.</p></div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-ink-soft mb-1">Drag rows to reorder how they appear on the homepage.</p>
          {items.map((f) => (
            <div key={f.id} draggable data-testid={`faq-row-${f.id}`}
              onDragStart={() => setDragId(f.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(f.id)}
              className={`flex items-start gap-3 bg-white rounded-2xl border p-4 transition-colors ${dragId === f.id ? "border-terracotta" : "border-bone"} ${!f.visible ? "opacity-55" : ""}`}>
              <GripVertical size={18} className="text-ink-soft/40 mt-1 cursor-grab active:cursor-grabbing shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] uppercase tracking-wide bg-[#EAE4DA] text-ink-soft px-2 py-0.5 rounded-full">{f.category}</span>
                  {!f.visible && <span className="text-[11px] text-ink-soft">Hidden</span>}
                </div>
                <p className="font-medium text-ink mt-1.5">{f.question}</p>
                <p className="text-sm text-ink-soft mt-0.5 line-clamp-2">{f.answer}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button data-testid={`faq-visible-${f.id}`} onClick={() => toggleVisible(f)} className="p-2 rounded-lg text-ink-soft hover:text-ink" title={f.visible ? "Hide" : "Show"}>
                  {f.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button data-testid={`faq-edit-${f.id}`} onClick={() => openEdit(f)} className="p-2 rounded-lg text-ink-soft hover:text-ink"><Pencil size={16} /></button>
                <button data-testid={`faq-delete-${f.id}`} onClick={() => remove(f)} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-sand">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">{editingId ? "Edit FAQ" : "New FAQ"}</DialogTitle>
            <DialogDescription className="text-ink-soft text-sm">This appears in the homepage FAQ section.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-ink font-medium">Question</Label>
              <Input data-testid="faq-question" value={form.question} onChange={(e) => set("question", e.target.value)} className="rounded-xl border-bone bg-white" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-ink font-medium">Answer</Label>
              <Textarea data-testid="faq-answer" value={form.answer} onChange={(e) => set("answer", e.target.value)} className="rounded-xl border-bone bg-white min-h-[120px]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label className="text-ink font-medium">Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="faq-category" className="rounded-xl border-bone bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-ink font-medium">Visible on site</Label>
                <div className="flex items-center h-10"><Switch data-testid="faq-visible-toggle" checked={form.visible} onCheckedChange={(c) => set("visible", c)} /></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="faq-save" onClick={save} disabled={saving} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white">
              {saving ? <Loader2 className="animate-spin" size={16} /> : (editingId ? "Save FAQ" : "Add FAQ")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
